import {
  FlexColumn, ModelForm, ThemeType, AutoSave,
  flattenErrors, useCombinedUser, UserRoles,
  isUserWithRoles, useIsMsalAuth, FormConfigurationProvider,
} from '@equifinance/shared';
import { css, ThemeProps, FlattenInterpolation } from 'styled-components';
import { PropsWithChildren } from 'react';
import { usePageConfigurationContext } from '../../contexts/pageConfiguration';
import { useChecklistAutoSave, useAccountContext, useChecklistContext } from '../../contexts';
import ChecklistToggleButtons from './Sections/ChecklistToggleButtons';
import AccountStage from '../AccountStage';
import { ApplicationPageType } from '../AccountTabNav';
import AccountTabPage from '../Page/AccountTabPage';
import ChecklistSidebar from './ChecklistSidebar';
import ProductSidebarFooter from '../Products/ProductSidebar/ProductSidebarFooter';
import PostOfferSection from './Sections/PostOfferSection';
import AMLSection from './Sections/AMLSection';
import BankruptcySection from './Sections/BankruptcySection';
import ProductSection from './Sections/ProductSection';
import ElectoralRollSection from './Sections/ElectoralRollSection';
import IncomeSection from './Sections/IncomeSection';
import PolicyShieldsSection from './Sections/PolicyShieldsSection';
import LandRegistrySection from './Sections/LandRegistrySection';
import ReportOnTitleSection from './Sections/ReportOnTitleSection';
import AdditionalSupportSection from './Sections/AdditionalSupportSection';
import PageErrors from '../Errors/PageErrors';
import SendOfferSection from './Sections/SendOfferSection';
import ValuationSection from './Sections/ValuationSection';
import OS1OS3Section from './Sections/OS1OS3Section';

export const CollapsibleHeaderCss = css<{ level: number }>`${({ theme, level }) => `
  background: ${theme.colours.warning[3]};
  
  ${level === 1 ? `
      height: ${theme.collapseHeaderHeight};
      background: ${theme.colours.primary[11]};
      color: ${theme.colours.primary[1]};
  ` : ''}

  ${level === 2 ? `
    height: ${theme.padding[8]};
    background: ${theme.colours.tertiary[5]};
    color: ${theme.colours.neutral[2]};
  ` : ''}
`}` as FlattenInterpolation<ThemeProps<ThemeType>>;

export enum ChecklistSection {
  POST_OFFER = 'Post Offer',
  POST_ACTIONS = 'Actions',
  POST_DATES = 'Dates',
  POST_DOCUMENTS = 'Post Offer Documents',
  NOTES_POST = 'Notes - Post',
  SEND_OFFER = 'Send Offer',
  SEND_OFFER_SECURITY_CALL = 'Security Call',
  SEND_OFFER_TITLE_DETAILS = 'Title Details',
  SEND_OFFER_OFFER = 'Offer',
  SEND_OFFER_OFFER_POSTAL = 'Wet Signature',
  SEND_OFFER_OFFER_ESIG = 'E-Signature',
  SEND_OFFER_DOCUMENTS = 'Send Offer Documents',
  NOTES_SEND_OFFER = 'Notes - Send Offer',
  AML_FRAUD = 'AML – Fraud Checks',
  AML_EQUIFAX = 'AML Equifax',
  VERIFICATION_HEADER = 'Verification Header',
  NOTES_AML = 'Notes - AML',
  BANKRUPTCY = 'Bankruptcy Check',
  NOTES_BANKRUPTCY = 'Notes - Bankruptcy',
  PRODUCT = 'Product',
  NOTES_PRODUCT = 'Notes - Product',
  OVERRIDE_NOTES_PRODUCT = 'Notes - Product (Override)',
  ELECTORAL_ROLL = 'Electoral Roll',
  INCOME = 'Income',
  POLICY_SHIELDS = 'Criteria / Policy conflicts',
  POLICY_SHIELDS_REFERRALS = 'Referrals',
  POLICY_SHIELDS_REJECTIONS = 'Rejections',
  NOTES_POLICY_SHIELDS = 'Notes - Policy Shields',
  OS1_OS3 = 'OS1 / OS3',
  NOTES_OS1_OS3 = 'NOTES - OS1 / OS3',
  REPORT_ON_TITLE = 'Report on Title (ROT)',
  ADDITIONAL_SUPPORT = 'Additional Support',
  VALUATION = 'Valuation',
  VALUATION_AVM_SEARCH = 'Additional AVM Required?',
  VALUATION_AVM_RESULTS = 'AVM Results',
  VALUATION_RESULTS = 'Valuation Results',
  VALUATION_DOCUMENTS = 'Valuation Documents',
  NOTES_VALUATION = 'Notes - Valuation',
}

export function NotesFormConfigurationAdapter({ children }: PropsWithChildren) {
  const { isReadOnly } = usePageConfigurationContext();
  const { account } = useAccountContext();
  const { status } = account ?? {};
  const isInternalUser = useIsMsalAuth();
  const { user } = useCombinedUser();
  let isNotesReadOnly = false;

  const isUnderwriter = isInternalUser && isUserWithRoles([UserRoles.UNDERWRITER], user);
  const isSalesSupport = isInternalUser && isUserWithRoles([UserRoles.SALES_SUPPORT], user);
  const isReadOnlyUser = isInternalUser && isUserWithRoles([UserRoles.READ_ONLY], user);

  if (isUnderwriter) {
    isNotesReadOnly = status === 'Complete';
  } else if (isSalesSupport || isReadOnlyUser) {
    isNotesReadOnly = isReadOnly;
  }

  return (
    <FormConfigurationProvider isReadOnlyInput={isNotesReadOnly}>
      {children}
    </FormConfigurationProvider>
  );
}

export default function ChecklistPage() {
  const {
    checklist, updateChecklist, handleToggleAll,
  } = useChecklistContext();

  const {
    errors, postOffer, sendOffer,
    fraudChecks, bankruptcyChecks, electoralRoll,
    income, product, policyShields,
    landRegistry, reportOnTitle, additionalSupport, valuation, os1os3,
  } = checklist ?? {};

  const update = (values: Record<string, any>, disableIncrementUpdateCount: boolean = false) => {
    Object.entries(values).forEach(([field, value]) => {
      updateChecklist(field, value, disableIncrementUpdateCount);
    });
  };
  const flattenedErrors = flattenErrors(checklist.errors);
  const flattenedWarnings = flattenErrors(checklist.warnings);

  return (
    <AccountTabPage
      subHeader={<AccountStage />}
      sidebar={<ChecklistSidebar />}
      sidebarFooter={<ProductSidebarFooter />}
      page={ApplicationPageType.CHECKLIST}
    >
      <AutoSave autoSave={useChecklistAutoSave}>
        <PageErrors id={checklist?.id} errors={flattenedErrors} warnings={flattenedWarnings} />
        <ModelForm model={checklist} update={update} errors={errors}>
          <FlexColumn>
            <ChecklistToggleButtons
              onExpandAll={() => handleToggleAll(true)}
              onCollapseAll={() => handleToggleAll(false)}
            />

            {postOffer && <PostOfferSection />}

            {sendOffer && <SendOfferSection />}

            {fraudChecks && <AMLSection />}

            {bankruptcyChecks && <BankruptcySection />}

            {electoralRoll && <ElectoralRollSection />}

            {income && <IncomeSection />}

            {product && <ProductSection />}

            {policyShields && <PolicyShieldsSection />}

            {landRegistry && <LandRegistrySection />}

            {reportOnTitle && <ReportOnTitleSection />}

            {additionalSupport && <AdditionalSupportSection />}

            {os1os3 && <OS1OS3Section />}

            {valuation && <ValuationSection />}

          </FlexColumn>
        </ModelForm>
      </AutoSave>
    </AccountTabPage>
  );
}
